import React, { useRef, useState, useEffect } from "react";
import { useHistory, Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Link,
  Image,
  IconButton,
  useDisclosure,
  Stack,
  Button,
  Collapse,
  Divider,
  useOutsideClick,
  HStack,
  Text,
  VStack,
  Icon,
  useBreakpointValue,
} from "@chakra-ui/react";
import { HiMenuAlt3 } from "react-icons/hi";
import AuthModal from "./AuthModal";
import UserProfileMenu from "./UserProfileMenu";
import { useAuth, logout } from "../auth";
import { useGoogleAuth } from "./common/google/GoogleAuthContext";
import { useOTP } from "./AdminPanelAccess/OTPContext";
import SettingsMenu from "./common/SettingsMenu";
import Translate from "../components/LanguageTranslation/Translate";
import logo from "../assets/images/New-Business-Logo.png";

const NavBar = ({
  bgColor = "black",
  linkColor = "white",
  styling,
  logoSrc,
  languageCodeColor,
  userProfileBgColor,
  onMobileMenuOpen,
  onMobileMenuClose,
  isMobileMenuOpen,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [logged] = useAuth();
  const { resetOTP } = useOTP();
  const { authState } = useGoogleAuth();
  const popupRef = useRef();

  useOutsideClick({
    ref: popupRef,
    handler: () => {
      if (isMenuOpen) onMenuClose(); // Close the menu if clicked outside
    },
  });

  const {
    isOpen: isMenuOpen,
    onOpen: onMenuOpen,
    onClose: onMenuClose,
  } = useDisclosure();
  const {
    isOpen: isAuthModalOpen,
    onOpen: onAuthModalOpen,
    onClose: onAuthModalClose,
  } = useDisclosure();

  const navLinks = [
    { to: "/ai-travel-planner", text: "Travel Planner" },
    { to: "/creators", text: "For Creators" },
    { to: "/businesses", text: "For Businesses" },
    { to: "/blogs", text: "Blogs" },
  ];

  const isUserLoggedIn = logged || authState.isLoggedIn;

  const logoutAndClear = () => {
    logout();
    const keysToRemove = [
      "username",
      "email",
      "perPage",
      "perPageFilter",
      "nPXg",
      "chat_messages",
      "conversation_id",
      "OTP_TOKEN",
      "profilepic",
      "selectedTab",
      "selectedTripPackingList",
      "selectedTripBudgetPlanner",
      "selectedCause",
    ];
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    resetOTP();
    // history.push("/login");
  };

  const [isScrolled, setIsScrolled] = useState(false);

  /* run once when the component mounts */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Chakra returns true on the current breakpoint */
  const isMobile = useBreakpointValue({ base: true, md: false });

  const mobileBg = isScrolled ? "white" : bgColor; // bgColor is your prop

  return (
    <Box
      position={{ base: "fixed", md: "fixed", lg: "static" }} // Fixed on base & md; static on lg
      top="0"
      left="0"
      right="0"
      w="100%" // Ensure full width
      bg={isMobile ? mobileBg : bgColor}
      zIndex="2000"
      p={4}
      // borderRadius="lg" // Optional: remove borderRadius on mobile if desired
    >
      <Flex h="35px" alignItems="center" justifyContent="space-between">
        {/* Left side: Logo + Nav Links (lg+) */}
        <Flex alignItems="center">
          {/* Logo */}
          <Link as={RouterLink} to="/home" borderRadius="xl" bg="white">
            <Image
              src={logo}
              alt="logo"
              width={{ base: "100px", md: "120px", lg: "120px" }}
              height={{ base: "40px", md: "45px", lg: "45px" }}
              objectFit="contain"
              pl={3}
              pr={3}
            />
          </Link>

          {/* Nav Links (only for lg screens) */}
          <Flex
            as="nav"
            alignItems="center"
            display={{ base: "none", md: "none", lg: "flex" }}
            ml={4}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={4}
              borderRadius="full"
              p={3}
              pl={4}
              pr={4}
            >
              {navLinks.map((link, index) => (
                <React.Fragment key={index}>
                  <Link
                    as={RouterLink}
                    to={link.to}
                    fontWeight="bold"
                    fontSize={{ base: "xs", md: "xs", lg: "sm" }}
                    color={linkColor}
                    fontFamily="Poppins, sans-serif"
                    textDecoration={
                      location.pathname === link.to ? "underline" : "none"
                    }
                    textDecorationThickness="1.5px"
                    textUnderlineOffset="4px"
                    _hover="none"
                  >
                    <Translate text={link.text} />
                  </Link>
                  {index < navLinks.length - 1 && (
                    <Divider
                      orientation="vertical"
                      height="20px"
                      borderColor={linkColor}
                    />
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Flex>
        </Flex>

        {/* Right side */}
        <Flex alignItems="center">
          <UserProfileMenu
            languageCodeColor={languageCodeColor}
            userProfileBgColor={userProfileBgColor}
            onLoginClick={onAuthModalOpen}
            mr={{ base: 2, md: 2, lg: 4 }}
            isScrolled={isScrolled}
          />

          {/* SettingsMenu + “Get Started” on lg+ */}
          <Flex
            display={{ base: "none", md: "none", lg: "flex" }}
            alignItems="center"
          >
            <SettingsMenu languageCodeColor={languageCodeColor} />
            <Button
              onClick={() => history.push("/ai-travel-planner")}
              bg="#FFC719"
              color="black"
              borderRadius="md"
              transition="transform 0.3s"
              _hover={{
                transform: "scale(1.1)",
                // border: "2px solid #FFC719",
                borderColor: "#FFC719",
                bg: "#FFC719",
              }}
              size={{ base: "xs", md: "sm", lg: "sm" }}
              fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              ml={2}
            >
              <Translate text="Get Started" />
            </Button>
          </Flex>

          {/* Hamburger Icon for base/md */}
          <Icon
            as={HiMenuAlt3}
            boxSize="8" // Or use w/h
            aria-label="Open Menu"
            transform="scaleX(-1)"
            display={{ base: "block", md: "block", lg: "none" }}
            color={isScrolled ? "black" : languageCodeColor}
            cursor="pointer"
            onClick={isMobileMenuOpen ? onMobileMenuClose : onMobileMenuOpen}
          />
        </Flex>
      </Flex>

      {/* Collapsible Menu for base/md */}
      <Collapse in={isMenuOpen} animateOpacity>
        <Box
          ref={popupRef}
          display={{ lg: "none" }}
          bg="white"
          p={4}
          mt={2}
          borderRadius="xl"
          w={{ base: "80%", md: "100%" }}
          ml="auto"
        >
          {/* 1️⃣  First row: first nav link + settings */}
          <Flex align="center" justifyContent="space-between" w="100%" mb={2}>
            {navLinks.length > 0 && (
              <Link
                as={RouterLink}
                to={navLinks[0].to}
                fontWeight="bold"
                onClick={navLinks[0].onClick ?? null}
                color="black"
              >
                <Translate text={navLinks[0].text} />
              </Link>
            )}

            <SettingsMenu showMobileDrawer={false} />
          </Flex>

          {/* 2️⃣  Remaining links + buttons */}
          <Stack as="nav" spacing={4} alignItems="flex-start">
            {navLinks.slice(1).map((link, index) => (
              <Link
                as={RouterLink}
                key={index}
                to={link.to}
                fontWeight="bold"
                onClick={link.onClick ?? null}
                color="black"
              >
                <Translate text={link.text} />
              </Link>
            ))}

            {isUserLoggedIn ? (
              <Button
                onClick={logoutAndClear}
                color="black"
                bg="white"
                borderRadius="md"
                border="1px solid black"
                _hover="none"
                size="sm"
              >
                <Translate text="Logout" />
              </Button>
            ) : (
              <Button
                onClick={onAuthModalOpen}
                color="black"
                bg="white"
                borderRadius="md"
                border="1px solid black"
                _hover="none"
                size="sm"
              >
                <Translate text="Sign in" />
              </Button>
            )}

            <Button
              onClick={() => history.push("/ai-travel-planner")}
              bg="black"
              color="white"
              borderRadius="md"
              transition="transform 0.3s"
              _hover={{
                transform: "scale(1.1)",
                // border: "2px solid #FFC719",
                borderColor: "#FFC719",
                bg: "black",
              }}
              size="sm"
            >
              <Translate text="Get Started" />
            </Button>
          </Stack>
        </Box>
      </Collapse>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={onAuthModalClose}
        redirectPath="/home"
      />
    </Box>
  );
};

export default NavBar;
